import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Sparkles, Shield, ArrowRight, LogIn, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { auth0Config } from '../../config/auth0';

interface LoginPageProps {}

export const LoginPage: React.FC<LoginPageProps> = () => {
  const { loginWithRedirect, isLoading, error } = useAuth0();

  const handleLogin = () => {
    // PRODUCTION DEBUG - Remove after fixing
    console.log('🔴 LOGIN BUTTON CLICKED - DEBUG INFO:');
    console.log('Domain being used:', auth0Config.domain);
    console.log('Client ID being used:', auth0Config.clientId);
    console.log('Redirect URI from config:', auth0Config.redirectUri);
    console.log('Redirect URI from env:', process.env.REACT_APP_AUTH0_REDIRECT_URI);
    console.log('Window location origin:', window.location.origin);
    console.log('Window location href:', window.location.href);
    
    const finalRedirectUri = auth0Config.redirectUri;
    console.log('🎯 FINAL redirect_uri being sent to Auth0:', finalRedirectUri);
    
    loginWithRedirect({
      authorizationParams: {
        redirect_uri: finalRedirectUri,
      },
    });
  };


  return (
    <div className="min-h-screen bg-stone-50 dark:bg-gray-900 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-700/25 dark:[mask-image:linear-gradient(0deg,rgba(255,255,255,0.1),rgba(255,255,255,0.5))]" />
      
      <div className="relative w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-6">
              <div className="relative">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <Sparkles className="w-10 h-10 text-primary animate-pulse" />
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-pink-500 to-violet-500 rounded-full animate-ping" />
                  </div>
                  <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 dark:from-white dark:via-blue-100 dark:to-white bg-clip-text text-transparent">
                    Deep Research
                  </h1>
                </div>
              </div>
            </div>
            <p className="text-lg text-stone-600 dark:text-slate-300 font-medium">
              Sign in to unlock the power of AI-driven research
            </p>
          </div>

          {/* Login Card */}
          <Card className="border shadow-2xl bg-stone-100 dark:bg-gray-800">
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center space-x-2">
                <Shield className="w-5 h-5 text-primary" />
                <span>Secure Authentication</span>
              </CardTitle>
              <CardDescription className="text-stone-600 dark:text-slate-300">
                Access your personalized research workspace
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Error Display */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-600 rounded-lg p-3"
                >
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-800 dark:text-red-200">
                      Authentication error: {error.message}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Login Button */}
              <Button 
                onClick={handleLogin}
                disabled={isLoading}
                size="lg"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  <>
                    <LogIn className="w-5 h-5 mr-2" />
                    Sign In
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              {/* Features Preview */}
              <div className="mt-6 pt-4 border-t border-stone-200 dark:border-gray-700">
                <p className="text-sm text-stone-600 dark:text-slate-400 text-center mb-3">
                  What you'll get:
                </p>
                <div className="space-y-2 text-xs text-stone-600 dark:text-slate-400">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    <span>In depth AI research</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    <span>Industry recommended sources</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    <span>BYOK - No subscription required</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <p className="text-center text-xs text-stone-500 dark:text-slate-500 mt-6">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </motion.div>
      </div>
    </div>
  );
};